/*
  Blair 2026-10-18 (founder: "find a scrolling cards component and use it so it has all the thought
  behind the implementation and we don't build from zero ... it needs to feel natural").

  The homepage slideshows (all widths) and the desktop collection product rows are Embla Carousel
  (v8, MIT, vendored in assets/embla-carousel*.umd.js). It replaces three home-grown layers that
  fought each other: Dawn's scroll-snap slider, drag-scroll.js (window mouseup, so a release outside
  the window left the row following the cursor) and the per-section drag/loop scripts.

  Embla owns drag, momentum, snapping, looping, RTL and the click-after-drag guard. This file only
  adapts the existing markup to it and keeps the theme's own controls (dots, pause button, arrows).

  Structure:
    slideshow:  .slideshow.slider (the clip box, also the root the video governor observes)
                  > .blair-embla__container (created here, moved by transform)
                    > .slideshow__slide ...
    rows:       slider-component > .blair-embla-vp (created here, the clip box)
                  > ul.slider (moved by transform) > li.grid__item ...
  The rows keep their <ul> as the moving list because the homepage top-up (blair-swipe-deck-v6.js)
  appends cards to it; a MutationObserver re-initialises Embla when that happens.
*/
(function () {
  'use strict';
  if (typeof window.EmblaCarousel !== 'function') return;

  var rtl = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktop = window.matchMedia && window.matchMedia('(min-width: 990px)');

  function noDragImages(root) {
    [].forEach.call(root.querySelectorAll('img, a'), function (el) { el.setAttribute('draggable', 'false'); });
  }

  function paintDots(dots, i, current) {
    dots.forEach(function (d, k) {
      var on = k === i;
      d.classList.toggle('slider-counter__link--active', on);
      if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
    });
    if (current) current.textContent = String(i + 1);
  }

  /* ---------------- slideshows ---------------- */
  function initSlideshow(comp) {
    var viewport = comp.querySelector('.slideshow.slider');
    if (!viewport || viewport.__blairEmbla) return;
    var slides = [].slice.call(viewport.querySelectorAll('.slideshow__slide'));
    if (slides.length < 2) return;

    var container = document.createElement('div');
    container.className = 'blair-embla__container';
    while (viewport.firstChild) container.appendChild(viewport.firstChild);
    viewport.appendChild(container);
    viewport.classList.add('blair-embla');
    viewport.scrollLeft = 0;
    noDragImages(container);

    var wantsAutoplay = viewport.getAttribute('data-autoplay') === 'true';
    var delay = (parseFloat(viewport.getAttribute('data-speed')) || 3) * 1000;
    var plugins = [];
    var autoplay = null;
    if (wantsAutoplay && typeof window.EmblaCarouselAutoplay === 'function') {
      // Dawn's slideshow-component already pauses on hover/focus, off screen and on the pause button by
      // calling its play()/pause(); those are pointed at this plugin below, so it starts stopped.
      autoplay = window.EmblaCarouselAutoplay({
        delay: delay, playOnInit: false, stopOnInteraction: true,
        stopOnMouseEnter: false, stopOnFocusIn: false
      });
      plugins.push(autoplay);
    }

    var api = window.EmblaCarousel(viewport, {
      container: container,
      slides: '.slideshow__slide',
      loop: true,
      align: 'center',
      direction: rtl ? 'rtl' : 'ltr',
      skipSnaps: true,
      duration: 28
    }, plugins);
    viewport.__blairEmbla = api;
    viewport.__blairAutoplay = autoplay;

    // Take over Dawn's autoplay and position-based dots.
    clearInterval(comp.autoplay);
    comp.autoplay = null;
    comp.autoRotateSlides = function () {};
    comp.update = function () {};
    comp.setSlideVisibility = function () {};
    comp.play = function () {
      viewport.setAttribute('aria-live', 'off');
      if (autoplay) autoplay.play();
    };
    comp.pause = function () {
      viewport.setAttribute('aria-live', 'polite');
      if (autoplay) autoplay.stop();
    };
    function wanted() { return !!autoplay && !reduce && comp.autoplayButtonIsSetToPlay !== false && !comp.blairPausedOffscreen; }
    if (wanted()) comp.play();
    // A drag stops the timer (stopOnInteraction). With a mouse, Dawn's own mouseleave resumes it (and
    // respects the pause button); a finger never "leaves", so after a swipe resume here, if wanted.
    var touched = false;
    viewport.addEventListener('touchstart', function () { touched = true; }, { passive: true });
    viewport.addEventListener('mousedown', function () { touched = false; });
    api.on('pointerUp', function () { if (touched && wanted()) setTimeout(function () { comp.play(); }, 0); });

    var dots = [].slice.call(comp.querySelectorAll('.slider-counter__link'));
    var current = comp.querySelector('.slider-counter--current');
    dots.forEach(function (d, k) {
      d.addEventListener('click', function (e) {
        e.preventDefault(); e.stopImmediatePropagation();
        api.scrollTo(k);
      }, true);
    });
    [].forEach.call(comp.querySelectorAll('.slider-button--prev, .slider-button--next'), function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopImmediatePropagation();
        if (b.classList.contains('slider-button--prev')) api.scrollPrev(); else api.scrollNext();
      }, true);
      b.removeAttribute('disabled');
    });
    function onSelect() { paintDots(dots, api.selectedScrollSnap(), current); }
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    onSelect();
  }

  /* ---------------- desktop collection rows ---------------- */
  function initRow(sc) {
    var list = sc.querySelector('ul.slider');
    if (!list || list.__blairEmbla) return;
    if (list.querySelectorAll(':scope > li').length < 2) return;

    var vp = document.createElement('div');
    vp.className = 'blair-embla-vp';
    list.parentNode.insertBefore(vp, list);
    vp.appendChild(list);
    list.classList.add('blair-embla-list');
    list.scrollLeft = 0;
    noDragImages(list);

    var api = window.EmblaCarousel(vp, {
      container: list,
      loop: true,
      align: 'start',
      direction: rtl ? 'rtl' : 'ltr',
      skipSnaps: true,
      duration: 28
    });
    list.__blairEmbla = api;
    sc.update = function () {};

    [].forEach.call(sc.querySelectorAll('.slider-button--prev, .slider-button--next'), function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopImmediatePropagation();
        if (b.classList.contains('slider-button--prev')) api.scrollPrev(); else api.scrollNext();
      }, true);
    });
    var current = sc.querySelector('.slider-counter--current');
    var total = sc.querySelector('.slider-counter--total');
    function onSelect() {
      if (current) current.textContent = String(api.selectedScrollSnap() + 1);
      if (total) total.textContent = String(api.slideNodes().length);
    }
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    onSelect();

    // The homepage top-up appends the rest of the collection to this list after first paint.
    var pending = 0;
    new MutationObserver(function () {
      if (pending) return;
      pending = requestAnimationFrame(function () {
        pending = 0;
        noDragImages(list);
        api.reInit();
      });
    }).observe(list, { childList: true });
  }

  // Founder 2026-10-18: "the drag sometimes gets stuck dragging even when I'm not clicking". Embla
  // ends a mouse drag on the document's mouseup. When the button is released where the page never
  // hears it (outside the window, over the browser chrome, after alt-tab), the next mousemove arrives
  // with no button held: end the drag right there, as if the mouseup had arrived.
  var mouseDragging = false;
  function release(x, y) {
    if (!mouseDragging) return;
    mouseDragging = false;
    document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, clientX: x || 0, clientY: y || 0, button: 0 }));
  }
  document.addEventListener('mousedown', function (e) {
    if (e.button === 0 && e.target.closest && e.target.closest('.blair-embla, .blair-embla-vp')) mouseDragging = true;
  }, true);
  document.addEventListener('mouseup', function () { mouseDragging = false; }, true);
  document.addEventListener('mousemove', function (e) { if (mouseDragging && e.buttons === 0) release(e.clientX, e.clientY); }, true);
  window.addEventListener('blur', function () { release(); });
  document.addEventListener('visibilitychange', function () { if (document.hidden) release(); });

  function initAll() {
    [].forEach.call(document.querySelectorAll('slideshow-component'), function (comp) {
      if (comp.querySelector('.slideshow.slider')) initSlideshow(comp);
    });
    if (desktop && desktop.matches && document.body.classList.contains('template-index')) {
      [].forEach.call(document.querySelectorAll('.collection slider-component'), initRow);
    }
  }

  function start() {
    var defs = [];
    if (window.customElements) {
      defs.push(customElements.whenDefined('slideshow-component'));
      defs.push(customElements.whenDefined('slider-component'));
    }
    Promise.all(defs).then(initAll, initAll);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
